import React, { Suspense, useEffect, useState, lazy } from 'react'
import { NotFound, Redirect, Route, Switch, useHistory } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { i18n } from '@fs/zion-locale'
import { Usabilla } from '@fs/zion-usabilla'
import { GroupsProvider } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { useUser } from '@fs/zion-user'
import getPermission from '@fs/zion-permissions'
import { NoticeLoading } from '@fs/zion-icon'
import { getActiveRole } from '@fs/zion-admin-mode'
import { useFeatureFlag } from '@fs/zion-flags'
import { AppLayout, colors } from '@fs/zion-ui'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import usePreloadPreferences from './services/usePreloadPreferences'
import utils from './helpers/utils'
import TreeChangesPage from './pages/TreeChangesPage'
import GroupDetailsPage from './pages/GroupDetailsPage'
import ConnectCreatePage from './pages/ConnectCreatePage'
import FamilyGroupsListPage from './pages/FamilyGroupsListPage'
import ResearchTreeListPage from './pages/ResearchTreeListPage'
import UnavailableDecorator from './components/UnavailableDecorator'
import { SubnavWrapper } from './components/SubnavWrapper'
import DataAdminProvider from './providers/DataAdminProvider'
import AppLoading from './components/AppLoading'
import CenteredLoadingSpinner from './components/CenteredLoadingSpinner'
import TreeIdToGroupId from './pages/TreeIdToGroupId'
import ManageTreesPage from './pages/ManageTreesPage'

// ---------------- Tree Import (migrated) lazy imports ----------------
// These were originally defined inside src/tree-import/App.js. We lazy load to keep initial bundle smaller.
const ComparePage = lazy(() => import('./tree-import/components/compare-page/ComparePage'))
const LandingPage = lazy(() => import('./tree-import/components/landing-page/LandingPage'))
const MatchesPage = lazy(() => import('./tree-import/components/matches-page/MatchesPage'))
const ChooseOldOrNewImportPage = lazy(() => import('./tree-import/components/import-page/ChooseOldOrNewImportPage'))
const RealTimeStats = lazy(() => import('./tree-import/components/admin/real-time-stats/RealTimeStats'))
const RecentTrees = lazy(() => import('./tree-import/components/admin/recent-trees/RecentTrees'))
const TreeStats = lazy(() => import('./tree-import/components/admin/tree-stats/TreeStats'))
const TreePersons = lazy(() => import('./tree-import/components/admin/tree-persons/TreePersons'))
const UserMatchesPage = React.lazy(() => import('./tree-import/components/matches-page/UserMatchesPage'))

const usabillaSupportedLanguages = {
  en: '//w.usabilla.com/9e116afd8c6f.js',
}
const usabillaCetSupportedLanguages = {
  en: `//w.usabilla.com/c15c8e8e96e0.js`,
}

// Wrapper component to allow the preference-fetching hook to run *before* the app loads, so that we catch everything on initial load, instead of piecemeal.
const TopLevelAppWrapper = ({ children }) => {
  // Pre-cache all preferences that are used in the app
  usePreloadPreferences()

  return (
    <GroupsProvider
      LoadingComponent={AppLoading}
      SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
      SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
    >
      {children}
    </GroupsProvider>
  )
}

const DataAdminProviderWrapper = ({ children }) => {
  const [role, setRole] = useState()

  useEffect(() => {
    let isCurrent = true
    getActiveRole()
      .then((adminType) => isCurrent && setRole(adminType))
      .catch(console.error)
    return () => {
      isCurrent = false
    }
  }, [])

  return <DataAdminProvider dataAdminRole={role}>{children}</DataAdminProvider>
}

const ResearcherToResearchTreeRedirect = () => {
  // Instead of using <Redirect from="/researcher*" to="/research-tree*" /> that does not keep query params, we do this instead.
  const history = useHistory()

  useEffect(() => {
    history.replace(
      `${history.location.pathname.replace('/researcher', '/research-tree')}${history.location.search}`,
      history.location.state
    )
  }, [history])
  return null
}

function UsabillaAction({ type = 'family' }) {
  const url = type === 'cet' ? usabillaCetSupportedLanguages[i18n.language] : usabillaSupportedLanguages[i18n.language]

  if (!url) return null
  return (
    // We hide Usabilla on mobile devices primarily to keep from accidentally obscuring the invitation QR code.
    <Usabilla
      communityLink={
        type === 'cet' ? undefined : 'https://community.familysearch.org/group/321-family-group-trees-test-group'
      }
      hideOnMobile
      mobileBreakpoint="sm"
      side="right"
      url={url}
    />
  )
}

// ---------------- Tree Import Routes (under /groups) ----------------
// These routes were migrated from the legacy tree import app. Previously they lived at /tree/import/*.
// Legacy server redirects now map /tree/import/* -> /groups/* (see server.js).
// Admin-only routes are guarded by permission checks below.

const AppRoutes = () => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [permissionChecked, setPermissionChecked] = useState(false)

  useEffect(() => {
    let isCurrent = true
    getPermission('AdminPermission', 'SubmittedTrees')
      .then(({ authorized }) => {
        if (isCurrent) {
          setIsAdmin(authorized)
          setPermissionChecked(true)
        }
        return undefined
      })
      .catch((e) => {
        console.error(e)
        setPermissionChecked(true)
      })
    return () => {
      isCurrent = false
    }
  }, [])

  const { signedIn } = useUser()
  const familyTabNames = Object.values(utils.tabs.family)
  const researchTreeTabNames = Object.values(utils.tabs.researchTree)

  const researchTreeRoutes = `/:subPage(research-tree)/:groupId?/:tab(${researchTreeTabNames.join('|')})?`
  const familyGroupRoutes = `/:subPage(family)/:groupId?/:tab(${familyTabNames.join('|')})?`
  const importRoutesOn = useFeatureFlag('treeWeb_gm_import_routes').isOn // feature flag still respected for /research-tree landing
  const manageTreesOn = useFeatureFlag('treeWeb_manageTrees').isOn

  if (!permissionChecked) return null

  return (
    <Switch>
      {/* Import user flows (now top-level within /groups) */}
      <TreeRoute requirePersonalData exact path="/files" component={ChooseOldOrNewImportPage} />
      <TreeRoute requirePersonalData exact path="/compare/:treeId" component={ComparePage} />
      <TreeRoute requirePersonalData exact path="/matches/user" component={UserMatchesPage} />
      <TreeRoute requirePersonalData exact path="/matches/:treeId" component={MatchesPage} />

      {/* Import admin routes */}
      {isAdmin && <TreeRoute exact path="/admin" component={RealTimeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/stats" component={RealTimeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/stats/:treeId" component={TreeStats} />}
      {isAdmin && <TreeRoute exact path="/admin/recent/:page/:size" component={RecentTrees} />}
      {isAdmin && (
        <TreeRoute exact path="/admin/persons/:treeId/:personStatus/:matchLevel/:page/:size" component={TreePersons} />
      )}

      {/* Main Group Management routes */}
      {manageTreesOn && (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path="/trees">
          <AppLayout backgroundColor={colors.gray03}>
            <ManageTreesPage />
          </AppLayout>
        </TreeRoute>
      )}
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path="/research-tree">
        <UnavailableDecorator isResearcherPage>
          {importRoutesOn ? <ChooseOldOrNewImportPage /> : <ResearchTreeListPage />}
          <UsabillaAction type="cet" />
        </UnavailableDecorator>
      </TreeRoute>
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path="/family">
        <UnavailableDecorator>
          <FamilyGroupsListPage />
          <UsabillaAction />
        </UnavailableDecorator>
      </TreeRoute>
      {[familyGroupRoutes].map((path) => (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} key={path} requirePersonalData exact path={path}>
          <UnavailableDecorator>
            <GroupDetailsPage />
            <UsabillaAction />
          </UnavailableDecorator>
        </TreeRoute>
      ))}
      {[researchTreeRoutes].map((path) => (
        <TreeRoute LoadingComponent={CenteredLoadingSpinner} key={path} requirePersonalData exact path={path}>
          <UnavailableDecorator isResearcherPage>
            <GroupDetailsPage />
            <UsabillaAction type="cet" />
          </UnavailableDecorator>
        </TreeRoute>
      ))}
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} path="/researcher">
        <ResearcherToResearchTreeRedirect />
      </TreeRoute>
      <TreeRoute
        LoadingComponent={CenteredLoadingSpinner}
        requirePersonalData
        exact
        path="/:subPage(research-tree)/:groupId/changes"
      >
        <UnavailableDecorator isResearcherPage>
          <TreeChangesPage />
          <UsabillaAction type="cet" />
        </UnavailableDecorator>
      </TreeRoute>
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path="/switch/:treeId">
        <TreeIdToGroupId />
      </TreeRoute>
      <TreeRoute LoadingComponent={CenteredLoadingSpinner} requirePersonalData exact path="/connect-create">
        <UnavailableDecorator>
          <ConnectCreatePage />
        </UnavailableDecorator>
      </TreeRoute>
      <Route exact path="/connect-create/done" />

      {/* Default import landing (signed-in users go to /family, others see landing page) */}
      <Route exact path="/" requirePersonalData>
        {signedIn ? (
          <Redirect to="/family" />
        ) : (
          <Suspense
            fallback={
              <div style={{ margin: '16px auto', display: 'flex', justifyContent: 'center' }}>
                <NoticeLoading size="lg" />
              </div>
            }
          >
            <LandingPage />
          </Suspense>
        )}
      </Route>
      {/* Group Management 404 inside /groups namespace */}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  )
}

export default function App() {
  return (
    <TopLevelAppWrapper>
      <SubnavWrapper />
      <main id="skipToMainContent">
        <DataAdminProviderWrapper>
          <ErrorBoundary>
            <Suspense fallback={<CenteredLoadingSpinner />}>
              <AppRoutes />
            </Suspense>
          </ErrorBoundary>
        </DataAdminProviderWrapper>
      </main>
    </TopLevelAppWrapper>
  )
}
